const DAYS_OF_WEEK = {
    0: 'dimanche', 1: 'lundi', 2: 'mardi', 3: 'mercredi',
    4: 'jeudi', 5: 'vendredi', 6: 'samedi', 7: 'dimanche',
}

const padTime = (h, m) => `${String(h).padStart(2, '0')}h${String(m).padStart(2, '0')}`

const isStar = f => f === '*' || f === '?'
const isInt = f => /^\d+$/.test(f)
const intervalOf = f => {
    const m = /^\*\/(\d+)$/.exec(f)
    return m ? parseInt(m[1], 10) : null
}

const dayLabel = d => {
    if (isInt(d)) return DAYS_OF_WEEK[parseInt(d, 10) % 8] ?? `jour ${d}`
    return null
}

const interpret = (minute, hour, dayOfMonth, month, dayOfWeek) => {
    const minInt = intervalOf(minute)
    const hourInt = intervalOf(hour)

    if (minInt && isStar(hour) && isStar(dayOfMonth) && isStar(month) && isStar(dayOfWeek)) {
        return minInt === 1 ? 'Toutes les minutes' : `Toutes les ${minInt} minutes`
    }

    if (isInt(minute) && hourInt && isStar(dayOfMonth) && isStar(month) && isStar(dayOfWeek)) {
        const m = parseInt(minute, 10)
        const base = hourInt === 1 ? 'Toutes les heures' : `Toutes les ${hourInt} heures`
        return m === 0 ? base : `${base} (à ${m} min)`
    }

    if (isStar(minute) && isStar(hour) && isStar(dayOfMonth) && isStar(month) && isStar(dayOfWeek)) {
        return 'Toutes les minutes'
    }

    // Minute fixe, toutes les heures (ex. "34 * * * *" -> toutes les heures à hh:34)
    if (isInt(minute) && isStar(hour) && isStar(dayOfMonth) && isStar(month) && isStar(dayOfWeek)) {
        const m = parseInt(minute, 10)
        return m === 0 ? 'Toutes les heures' : `Toutes les heures à hh:${String(m).padStart(2, '0')}`
    }

    // Jour du mois sur une plage de 7 jours + jour de semaine (cron4j : tous les champs doivent correspondre)
    // => n-ième jour de la semaine du mois (ex. "5 14 1-7 * 6" -> le 1er samedi du mois à 14h05)
    const domRange = /^(\d+)-(\d+)$/.exec(dayOfMonth)
    if (isInt(minute) && isInt(hour) && domRange && isStar(month) && isInt(dayOfWeek)
        && parseInt(domRange[2], 10) - parseInt(domRange[1], 10) === 6 && parseInt(domRange[2], 10) % 7 === 0) {
        const nth = parseInt(domRange[2], 10) / 7
        const day = dayLabel(dayOfWeek)
        return `Le ${nth === 1 ? '1er' : `${nth}e`} ${day} du mois à ${padTime(parseInt(hour, 10), parseInt(minute, 10))}`
    }

    if (isInt(minute) && isInt(hour) && isStar(dayOfMonth) && isStar(month) && isStar(dayOfWeek)) {
        return `Tous les jours à ${padTime(parseInt(hour, 10), parseInt(minute, 10))}`
    }

    if (isInt(minute) && isInt(hour) && isStar(dayOfMonth) && isStar(month) && !isStar(dayOfWeek)) {
        const time = padTime(parseInt(hour, 10), parseInt(minute, 10))
        if (isInt(dayOfWeek)) {
            const d = dayLabel(dayOfWeek)
            return d ? `Chaque ${d} à ${time}` : `Cron : ${dayOfWeek} jour de la semaine à ${time}`
        }
        if (/^\d+(,\d+)+$/.test(dayOfWeek)) {
            const days = dayOfWeek.split(',').map(dayLabel).filter(Boolean).join(', ')
            return `${days} à ${time}`
        }
        const range = /^(\d+)-(\d+)$/.exec(dayOfWeek)
        if (range) {
            return `Du ${dayLabel(range[1])} au ${dayLabel(range[2])} à ${time}`
        }
    }

    if (isInt(minute) && isInt(hour) && isInt(dayOfMonth) && isStar(month) && isStar(dayOfWeek)) {
        return `Le ${parseInt(dayOfMonth, 10)} de chaque mois à ${padTime(parseInt(hour, 10), parseInt(minute, 10))}`
    }

    return null
}

const toFiveFields = (parts) => {
    if (parts.length === 5) return parts
    if (parts.length === 6) return parts.slice(1)
    if (parts.length === 7) return parts.slice(1, 6)
    return null
}

const singleCronToHuman = (expr) => {
    const fields = toFiveFields(expr.trim().split(/\s+/))
    if (!fields) return expr

    const human = interpret(...fields)
    return human ?? expr
}

// Plusieurs programmations peuvent être séparées par ";" (cf. job-service ScheduledJobs).
const cronToHuman = (expr) => {
    if (!expr || typeof expr !== 'string') return ''
    return expr.split(';')
        .map(e => e.trim())
        .filter(e => e)
        .map(singleCronToHuman)
        .join(' ; ')
}

export { cronToHuman }
